import Check from '@mui/icons-material/Check';
import ChevronRight from '@mui/icons-material/ChevronRight';
import FilterList from '@mui/icons-material/FilterList';
import { buttonClasses, paperClasses } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { AccountType, InvoiceCategoryFilter } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { debounce, snakeCase, uniqBy } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import AppliedFiltersChips from '~components/Filters/AppliedFiltersChips';
import FilterMenuItem, { FilterMenuOption } from '~components/Filters/FilterMenuItem';
import Menu from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';
import { rateTypeOptions } from '~constants/enums';
import { InfiniteQuery } from '~queries/constants';
import useAccountsInfiniteQuery from '~queries/useAccountsInfiniteQuery';
import useProjectsInfiniteQuery from '~queries/useProjectsInfiniteQuery';
import useRateTypesInfiniteQuery from '~queries/useRateTypesInfiniteQuery';
import useWaypointInfiniteQuery from '~queries/useWaypointInfiniteQuery';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  payableInvoiceCategories,
  receivableInvoiceCategories,
} from '../../../DriverPay/constants';

const QUERY_PREFIX = 'DriverPayPage_SettlementsFilters';

enum SettlementsAvailableFilter {
  PROJECTS = 'projects',
  RATE_TYPES = 'rate_types',
  CUSTOMERS = 'customers',
  DROPOFF_SITES = 'dropoff_sites',
  VENDORS = 'vendors',
  PICKUP_SITES = 'pickup_sites',
}

type SearchQueryState = {
  input: string;
  debounced: string;
};

const settlementFilterKeyBySelectedFilter: Record<SettlementsAvailableFilter, string> = {
  [SettlementsAvailableFilter.PROJECTS]: 'project_ids',
  [SettlementsAvailableFilter.RATE_TYPES]: 'rate_types',
  [SettlementsAvailableFilter.CUSTOMERS]: 'customer_account_ids',
  [SettlementsAvailableFilter.DROPOFF_SITES]: 'dropoff_site_ids',
  [SettlementsAvailableFilter.VENDORS]: 'vendor_account_ids',
  [SettlementsAvailableFilter.PICKUP_SITES]: 'pickup_site_ids',
};

export default function SettlementsFilters() {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const [searchParams, setSearchParams] = useSearchParams();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<SettlementsAvailableFilter>();
  const [searchQuery, setSearchQuery] = useState<SearchQueryState>({
    input: '',
    debounced: '',
  });

  const category = searchParams.get('category') as InvoiceCategoryFilter | null;
  const isPayableCategory = useMemo(() => {
    return Boolean(
      category && payableInvoiceCategories.includes(category as InvoiceCategoryFilter),
    );
  }, [category]);

  const isReceivableCategory = useMemo(() => {
    return Boolean(
      category && receivableInvoiceCategories.includes(category as InvoiceCategoryFilter),
    );
  }, [category]);

  const availableFilters = useMemo(() => {
    return [
      SettlementsAvailableFilter.PROJECTS,
      SettlementsAvailableFilter.RATE_TYPES,
      ...(isReceivableCategory ? [SettlementsAvailableFilter.CUSTOMERS] : []),
      ...(isReceivableCategory ? [SettlementsAvailableFilter.DROPOFF_SITES] : []),
      ...(isPayableCategory ? [SettlementsAvailableFilter.VENDORS] : []),
      ...(isPayableCategory ? [SettlementsAvailableFilter.PICKUP_SITES] : []),
    ];
  }, [isReceivableCategory, isPayableCategory]);

  const appliedFilters = useMemo(() => {
    const filters: Record<string, string[]> = {};

    Object.entries(settlementFilterKeyBySelectedFilter).forEach(([, paramKey]) => {
      const value = searchParams.get(paramKey);
      if (value) {
        filters[paramKey] = value.split('|');
      }
    });

    return filters;
  }, [searchParams]);

  const customersQuery = useAccountsInfiniteQuery(
    `${QUERY_PREFIX}_customers`,
    companyId,
    AccountType.CUSTOMER,
  );
  const vendorsQuery = useAccountsInfiniteQuery(
    `${QUERY_PREFIX}_vendors`,
    companyId,
    AccountType.VENDOR,
  );
  const projectsQuery = useProjectsInfiniteQuery(`${QUERY_PREFIX}_projects`, companyId);
  const rateTypesQuery = useRateTypesInfiniteQuery();

  const pickupSitesQuery = useWaypointInfiniteQuery(
    `${QUERY_PREFIX}_pickupSites`,
    companyId,
    'pickup',
    {},
  );
  const dropoffSitesQuery = useWaypointInfiniteQuery(
    `${QUERY_PREFIX}_dropoffSites`,
    companyId,
    'drop_off',
    {},
  );

  const queriesBySettlementsAvailableFilter: Record<
    SettlementsAvailableFilter,
    InfiniteQuery
  > = {
    [SettlementsAvailableFilter.CUSTOMERS]: customersQuery,
    [SettlementsAvailableFilter.DROPOFF_SITES]: dropoffSitesQuery,
    [SettlementsAvailableFilter.PICKUP_SITES]: pickupSitesQuery,
    [SettlementsAvailableFilter.PROJECTS]: projectsQuery,
    [SettlementsAvailableFilter.RATE_TYPES]: rateTypesQuery,
    [SettlementsAvailableFilter.VENDORS]: vendorsQuery,
  };

  const activeQuery = useMemo(() => {
    if (!selectedFilter) {
      return null;
    }

    return queriesBySettlementsAvailableFilter[selectedFilter];
  }, [selectedFilter, queriesBySettlementsAvailableFilter]);

  const searchValueChangeCallback = useMemo(() => {
    if (
      activeQuery?.isDummyQuery ||
      selectedFilter === SettlementsAvailableFilter.RATE_TYPES
    ) {
      return undefined;
    }

    return (value: string) => setSearchQuery((state) => ({ ...state, input: value }));
  }, [activeQuery?.isDummyQuery, selectedFilter]);

  const selectedOptions = useMemo(() => {
    if (!selectedFilter) {
      return [];
    }

    const key = settlementFilterKeyBySelectedFilter[selectedFilter];
    const options = appliedFilters[key] ?? [];

    return options;
  }, [selectedFilter, appliedFilters]);

  const handleSelectedOptionsChange = useCallback(
    (options: string[]) => {
      if (selectedFilter) {
        const key = settlementFilterKeyBySelectedFilter[selectedFilter];

        if (options.length === 0) {
          setSearchParams((params) => {
            params.delete(key);
            return params;
          });
        } else {
          const value = options.join('|');
          setSearchParams((params) => {
            params.set(key, value);
            return params;
          });
        }
      }
    },
    [selectedFilter, setSearchParams],
  );

  const debouncedSetSearchQuery = useRef(
    debounce((value: string) => {
      setSearchQuery((state) => ({ ...state, debounced: value }));
    }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS),
  ).current;

  useEffect(() => {
    if (selectedFilter === SettlementsAvailableFilter.RATE_TYPES) {
      // Rate types are static, no need to fetch
      return;
    }
    activeQuery?.fetch(searchQuery.debounced);
  }, [activeQuery?.fetch, searchQuery.debounced, selectedFilter]);

  useEffect(() => {
    debouncedSetSearchQuery(searchQuery.input);

    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, [debouncedSetSearchQuery, searchQuery.input]);

  useEffect(() => {
    if (!isMenuOpen) {
      setSelectedFilter(undefined);
      setSearchQuery((state) => ({ ...state, input: '' }));
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (!selectedFilter) {
      Object.values(queriesBySettlementsAvailableFilter).forEach((query) => {
        query.clear();
      });
    }
  }, [selectedFilter]);

  const [filterChipsCollectedData, setFilterChipsCollectedData] = useState<
    Record<string, FilterMenuOption[]>
  >({});

  useEffect(() => {
    if (selectedFilter && activeQuery?.data) {
      setFilterChipsCollectedData((state) => {
        const key = settlementFilterKeyBySelectedFilter[selectedFilter];
        const updatedEntry = uniqBy(
          [...(state[key] ?? []), ...activeQuery.data],
          (option) => option.value,
        );

        const newCollectedData = {
          ...state,
          [key]: updatedEntry.length > 0 ? updatedEntry : [],
        };

        return newCollectedData;
      });
    }
  }, [selectedFilter, activeQuery?.data]);

  const hasFiltersApplied = useMemo(() => {
    return Object.values(appliedFilters).some((filter) => filter.length > 0);
  }, [appliedFilters]);

  const handleRemoveAllFilters = useCallback(() => {
    setSearchParams((params) => {
      Object.values(settlementFilterKeyBySelectedFilter).forEach((key) => {
        params.delete(key);
      });
      return params;
    });
  }, [setSearchParams]);

  const handleRemoveOneFilter = useCallback(
    (filter: string) => {
      setSearchParams((params) => {
        params.delete(filter);
        return params;
      });
    },
    [setSearchParams],
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Menu
        sx={{ [`& .${paperClasses.root}`]: { width: '250px' } }}
        onOpenStateChanged={setIsMenuOpen}
        menuTrigger={
          <SmallButton
            size="small"
            startIcon={<FilterList />}
            sx={(theme) => ({
              [`&.${buttonClasses.root}`]: {
                bgcolor: 'background.paper',
                color: theme.palette.text.primary,
                border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                borderRadius: theme.brandV2.borderRadius,
                ...(hasFiltersApplied
                  ? {
                      borderBottomLeftRadius: theme.brandV2.borderRadius,
                      borderBottomRightRadius: 0,
                      borderTopLeftRadius: theme.brandV2.borderRadius,
                      borderTopRightRadius: 0,
                    }
                  : { borderRadius: theme.brandV2.borderRadius }),
              },
            })}
          >
            {t('common.filters')}
          </SmallButton>
        }
      >
        {selectedFilter && (
          <FilterMenuItem
            focusSearchFieldOnMount
            loadingReason={
              selectedFilter === SettlementsAvailableFilter.RATE_TYPES
                ? undefined
                : activeQuery?.fetchReason
            }
            onFetchMore={
              selectedFilter === SettlementsAvailableFilter.RATE_TYPES
                ? undefined
                : activeQuery?.fetchMore
            }
            onSearchValueChange={searchValueChangeCallback}
            onSelectedOptionsChange={handleSelectedOptionsChange}
            options={
              selectedFilter === SettlementsAvailableFilter.RATE_TYPES
                ? searchQuery.input
                  ? rateTypeOptions.filter((option) =>
                      option.label
                        .toLowerCase()
                        .includes(searchQuery.input.toLowerCase()),
                    )
                  : rateTypeOptions
                : (activeQuery?.data ?? [])
            }
            searchValue={searchQuery.input}
            selectAllOptionLabel={`${t('dispatch.dispatch_v2.filters.all_entities', {
              entity: t(`dispatch.dispatch_v2.filters.${snakeCase(selectedFilter)}`),
            })}`}
            selectedOptions={selectedOptions}
          />
        )}

        {!selectedFilter &&
          availableFilters.map((filter) => (
            <MenuItem key={filter} onClick={() => setSelectedFilter(filter)}>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={(theme) => ({ color: theme.brandV2.colors.treadBlack })}
                >
                  {t(`dispatch.dispatch_v2.filters.${snakeCase(filter)}`)}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {Boolean(
                    appliedFilters[settlementFilterKeyBySelectedFilter[filter]],
                  ) && (
                    <Check
                      sx={(theme) => ({
                        color: theme.brandV2.colors.treadOrange,
                        fontSize: '16px',
                      })}
                    />
                  )}

                  <ChevronRight sx={{ fontSize: '16px' }} />
                </Box>
              </Box>
            </MenuItem>
          ))}
      </Menu>

      <AppliedFiltersChips
        appliedFilters={appliedFilters}
        collectedData={filterChipsCollectedData}
        onRemoveAllFilters={handleRemoveAllFilters}
        onRemoveFilter={handleRemoveOneFilter}
      />
    </Box>
  );
}
